import { Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { RecommendedAutomationsService } from './recommended-automations.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';
import {
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  setDropdownAriaLabel
} from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-recommended-automations',
    templateUrl: './recommended-automations.component.html',
    styleUrls: ['./recommended-automations.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RecommendedAutomationsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  filterParams: any = {};
  baseURLs: any = {};
  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  template = '';
  templateOptions: string[] = [];
  templateLoading = false;
  templateError = false;

  userm1 = '';
  userDescError = false;

  schdmt = '';
  schdmtype: string[] = [];
  schdmloading = false;
  typeError = false;

  multiplier = '';
  MultiplierError = false;

  tbbsacctval = '';
  tbbsacct: string[] = [];
  tbbsLoading = false;
  tbbsError = false;

  mainmodaldata: any[] = [];
  schdmtypedata: any[] = [];
  tbbsacctdata: any[] = [];

  readonly preferredLabel = '----------Preferred Templates---------';
  readonly nonPreferredLabel = '-------Non Preferred Templates-------';
  readonly emptyTemplate = '';

  /**
   * p-dropdown options as STABLE fields (not getters) to avoid creating new
   * arrays/objects on every change-detection tick. With ChangeDetectionStrategy.OnPush
   * + manual cdr.detectChanges() that pattern can wytrącać p-dropdown ze stanu zaznaczenia.
   * Refresh these via rebuildDropdownOptions() whenever the source arrays change.
   */
  templateOptionsForDropdown: { label: string; value: string }[] = [];
  schdmtypeForDropdown: { label: string; value: string }[] = [];
  tbbsacctForDropdown: { label: string; value: string }[] = [];
  selectedTemplateItem: any = null;
  selectedSchdmItem: any = null;
  selectedTbbsItem: any = null;

  private rebuildDropdownOptions(target: 'template' | 'schdm' | 'tbbs' | 'all' = 'all'): void {
    if (target === 'template' || target === 'all') {
      this.templateOptionsForDropdown = (this.templateOptions || []).map(t => ({ label: t, value: t }));
      this.selectedTemplateItem = findAppkitItemByValue(this.templateOptionsForDropdown, this.template);
    }
    if (target === 'schdm' || target === 'all') {
      this.schdmtypeForDropdown = (this.schdmtype || []).map(t => ({ label: t, value: t }));
      this.selectedSchdmItem = findAppkitItemByValue(this.schdmtypeForDropdown, this.schdmt);
    }
    if (target === 'tbbs' || target === 'all') {
      this.tbbsacctForDropdown = (this.tbbsacct || []).map(t => ({ label: t, value: t }));
      this.selectedTbbsItem = findAppkitItemByValue(this.tbbsacctForDropdown, this.tbbsacctval);
    }
  }

  onTemplateDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTemplateItem = selected;
    this.template = selected?.value ?? '';
    this.onTemplateChange();
  }

  onSchdmDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedSchdmItem = selected;
    this.schdmt = selected?.value ?? '';
    this.onTypeChange();
  }

  onTbbsDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTbbsItem = selected;
    this.tbbsacctval = selected?.value ?? '';
    this.onTbbsChange();
  }

  onTemplateSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) this.selectedTemplateItem = selected;
  }

  onSchdmSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) this.selectedSchdmItem = selected;
  }

  onTbbsSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) this.selectedTbbsItem = selected;
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: RecommendedAutomationsService,
    private gtwSnackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    if (!this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.showFormLoader = true;
      this.cdr.detectChanges();
      this.template = (this.rowData.SCHDM_TEMPLATE_ID != null && this.rowData.SCHDM_STD_DESC != null)
        ? `${this.rowData.SCHDM_TEMPLATE_ID} - ${this.rowData.SCHDM_STD_DESC}`
        : '';
      this.userm1 = this.rowData.SCHDM_USER_DESC ?? '';
      this.multiplier = this.rowData.MULTIPLIER != null ? String(this.rowData.MULTIPLIER) : '';
      this.schdmt = this.rowData.SCHDM_TYPE_DESC ?? '';
      this.tbbsacctval = (this.rowData.TBBS_ACCT != null && this.rowData.TBBS_ACCT_DESC != null)
        ? `${this.rowData.TBBS_ACCT}-${this.rowData.TBBS_ACCT_DESC}`
        : (this.rowData.TBBS_ACCT ?? '');
      this.templateOptions = [];
      this.mainmodaldata = [];
      this.schdmtype = [];
      this.schdmtypedata = [];
      this.tbbsacct = [];
      this.tbbsacctdata = [];
      this.rebuildDropdownOptions('all');
      this.loadTemplateData();
    } catch (e) {
      console.error('Error in init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private loadTemplateData(): void {
    this.templateLoading = true;
    this.service.loadTemplateData(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(res?.errorMessage || 'Failed to load template data', 'gtw-snackbar--red');
          this.templateLoading = false;
          this.showFormLoader = false;
          this.cdr.detectChanges();
          return;
        }
        this.mainmodaldata = res.jsonObject || [];
        this.templateOptions = _.uniq((this.mainmodaldata as any[]).map((x: any) => x.SCHDM_TEMPLATE));
        this.rebuildDropdownOptions('template');
        this.templateLoading = false;
        this.loadSchdmTypeInner();
      },
      error: (err) => {
        this.gtwSnackbar.openSnackBar('Error loading template data', 'gtw-snackbar--red');
        this.templateLoading = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadSchdmTypeInner(): void {
    this.schdmloading = true;
    this.service.loadSchdmType(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.templateIdForCurrentTemplate()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(res?.errorMessage || 'Failed to load SchdM type', 'gtw-snackbar--red');
          this.schdmloading = false;
          this.showFormLoader = false;
          this.cdr.detectChanges();
          return;
        }
        this.schdmtypedata = res.jsonObject || [];
        this.schdmtype = _.uniq(this.schdmtypedata.map((x: any) => x.SCHDM_TYPE));
        this.rebuildDropdownOptions('schdm');
        this.schdmloading = false;
        this.checkTypeError();
        this.loadTbbsDataInner();
      },
      error: (err) => {
        this.gtwSnackbar.openSnackBar('Error loading SchdM type', 'gtw-snackbar--red');
        this.schdmloading = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadTbbsDataInner(): void {
    this.tbbsLoading = true;
    this.service.loadTbbsData(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.templateIdForCurrentTemplate(),
      this.schdmKeyForCurrentType()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(res?.errorMessage || 'Failed to load TBBS data', 'gtw-snackbar--red');
        } else {
          this.applyTbbsResponse(res.jsonObject || [], 'row');
        }
        this.tbbsLoading = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.gtwSnackbar.openSnackBar('Error loading TBBS data', 'gtw-snackbar--red');
        this.tbbsLoading = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Shared success path for wfk6zt: store accounts, choose the value, then rebuild
   * AppKit options so selectedTbbsItem is resolved against the final tbbsacctval.
   */
  private applyTbbsResponse(accounts: any[], mode: 'row' | 'first'): void {
    this.tbbsacctdata = accounts || [];
    this.tbbsacct = _.uniq(this.tbbsacctdata.map((x: any) => x.TAX_STREAM_ACCT));
    if (mode === 'row') {
      this.applyTbbsValueFromRowData();
    } else if (this.tbbsacct.length > 0 && this.tbbsacctdata[0]) {
      this.tbbsacctval = this.tbbsacctdata[0].TAX_STREAM_ACCT ?? '';
    } else {
      this.tbbsacctval = '';
    }
    this.rebuildDropdownOptions('tbbs');
    this.tbbsError = this.tbbsacctval === '' || this.tbbsacctval === undefined;
  }

  /**
   * After TBBS options load, set dropdown value to rowData value if it exists.
   * Options use TAX_STREAM_ACCT; rowData has TBBS_ACCT / TBBS_ACCT_DESC - match so the dropdown shows the selected value.
   * Does not rebuild options; caller must call rebuildDropdownOptions('tbbs') after this.
   */
  private applyTbbsValueFromRowData(): void {
    const hasRowValue = this.rowData?.TBBS_ACCT != null || (this.rowData?.TBBS_ACCT_DESC != null && this.rowData?.TBBS_ACCT != null);
    if (!hasRowValue) return;
    const rowCode = this.rowData?.TBBS_ACCT != null ? String(this.rowData.TBBS_ACCT).trim() : '';
    const rowDisplay = (this.rowData?.TBBS_ACCT != null && this.rowData?.TBBS_ACCT_DESC != null)
      ? `${this.rowData.TBBS_ACCT}-${this.rowData.TBBS_ACCT_DESC}`.trim()
      : rowCode;
    if (this.tbbsacctdata?.length) {
      const match = this.tbbsacctdata.find((x: any) => {
        const v = x.TAX_STREAM_ACCT != null ? String(x.TAX_STREAM_ACCT).trim() : '';
        return v === rowDisplay || v === rowCode || (rowCode && v.startsWith(rowCode));
      });
      if (match?.TAX_STREAM_ACCT != null) {
        this.tbbsacctval = match.TAX_STREAM_ACCT;
        return;
      }
    }
    // No match in API options: use rowData value and ensure it appears in the list so dropdown can show it
    const valueToShow = rowDisplay || rowCode;
    if (valueToShow && !this.tbbsacct.includes(valueToShow)) {
      this.tbbsacct = [valueToShow, ...this.tbbsacct];
    }
    this.tbbsacctval = valueToShow;
  }

  private templateIdForCurrentTemplate(): string {
    for (const a of this.mainmodaldata) {
      if (a.SCHDM_TEMPLATE === this.template) {
        return a.SCHDM_TEMPLATE_ID ?? '';
      }
    }
    return '';
  }

  private schdmKeyForCurrentType(): string {
    for (const lst of this.schdmtypedata) {
      if (this.schdmt === lst.SCHDM_TYPE) {
        return lst.SCHDM_TYPE_SAVE || lst.SCHDM_TYPE || '';
      }
    }
    return '';
  }

  isTemplateDisabled(): boolean {
    return this.template === this.emptyTemplate ||
      this.template === this.preferredLabel ||
      this.template === this.nonPreferredLabel;
  }

  onTemplateChange(): void {
    for (const ag of this.mainmodaldata) {
      if (ag.SCHDM_TEMPLATE === this.template) {
        this.userm1 = ag.USER_DESC ?? '';
        break;
      }
    }
    this.checkTemplateError();
    this.checkUserDesc();
    this.loadSchdmType();
  }

  loadSchdmType(): void {
    const schdmTemp = this.templateIdForCurrentTemplate();
    this.schdmtype = [];
    this.schdmloading = true;
    this.schdmt = '';
    this.tbbsacctval = '';
    this.tbbsacct = [];
    this.tbbsacctdata = [];
    this.rebuildDropdownOptions('schdm');
    this.rebuildDropdownOptions('tbbs');
    this.cdr.detectChanges();

    this.service.loadSchdmType(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams,
      this.rowData,
      schdmTemp
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(res?.errorMessage || 'Failed to load SchdM type', 'gtw-snackbar--red');
        } else {
          this.schdmtypedata = res.jsonObject || [];
          this.schdmtype = _.uniq(this.schdmtypedata.map((x: any) => x.SCHDM_TYPE));
          // Set default type before rebuilding AppKit selection so selectedSchdmItem resolves.
          if (this.schdmtype.length > 0 && this.schdmtypedata[0]) {
            this.schdmt = this.schdmtypedata[0].SCHDM_TYPE_DEFAULT ?? '';
          }
          this.rebuildDropdownOptions('schdm');
          this.loadTbbsAfterTypeChange();
        }
        this.schdmloading = false;
        this.checkTypeError();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.gtwSnackbar.openSnackBar('Error loading SchdM type', 'gtw-snackbar--red');
        this.schdmloading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadTbbsAfterTypeChange(): void {
    this.tbbsLoading = true;
    this.service.loadTbbsData(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.templateIdForCurrentTemplate(),
      this.schdmKeyForCurrentType()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res?.callSuccess === '1') {
          this.applyTbbsResponse(res.jsonObject || [], 'first');
        }
        this.tbbsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.tbbsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onTypeChange(): void {
    this.checkTypeError();
    this.tbbsacctval = '';
    this.tbbsacct = [];
    this.tbbsacctdata = [];
    this.rebuildDropdownOptions('tbbs');
    this.tbbsLoading = true;
    this.service.loadTbbsData(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.templateIdForCurrentTemplate(),
      this.schdmKeyForCurrentType()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res?.callSuccess === '1') {
          this.applyTbbsResponse(res.jsonObject || [], 'first');
        }
        this.tbbsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.tbbsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  checkTemplateError(): void {
    this.templateError = this.template === '' || this.template === undefined;
    this.cdr.detectChanges();
  }

  checkTypeError(): void {
    this.typeError = this.schdmt === '' || this.schdmt === undefined;
    this.cdr.detectChanges();
  }

  checkUserDesc(): void {
    this.userDescError = this.userm1 === '' || this.userm1 === undefined;
    this.cdr.detectChanges();
  }

  onTbbsChange(): void {
    this.tbbsError = this.tbbsacctval === '' || this.tbbsacctval === undefined;
    this.cdr.detectChanges();
  }

  checkMultiplier(): void {
    const raw = this.multiplier;
    const isEmpty = raw === '' || raw === undefined || raw === null;
    const isNumeric = !isEmpty && !isNaN(Number(String(raw).trim()));
    this.MultiplierError = isEmpty || !isNumeric;
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    this.checkTemplateError();
    this.checkUserDesc();
    this.checkTypeError();
    this.checkMultiplier();
    this.onTbbsChange();
    if (this.templateError || this.userDescError || this.typeError || this.MultiplierError || this.tbbsError) {
      this.gtwSnackbar.openSnackBar('Please correct the errors below', 'gtw-snackbar--red');
      return;
    }

    let newtemplate_key: string | undefined;
    let new_schdm_key: string | undefined;
    let new_tbbs_acct_key: string | undefined;

    for (const k of this.mainmodaldata) {
      if (this.template === (k as any).SCHDM_TEMPLATE) {
        newtemplate_key = (k as any).SCHDM_TEMPLATE_ID;
        break;
      }
    }
    for (const m of this.schdmtypedata) {
      if (this.schdmt === (m as any).SCHDM_TYPE) {
        new_schdm_key = (m as any).SCHDM_TYPE_SAVE;
        break;
      }
    }
    for (const n of this.tbbsacctdata) {
      if (this.tbbsacctval === (n as any).TAX_STREAM_ACCT) {
        new_tbbs_acct_key = (n as any).TAX_STREAM_ACCT_SAVE;
        break;
      }
    }

    const g = this.globalService?.globalParams || this.globalService || {};
    const dummyJson: any = {
      TAX_YEAR: g.tax_year ?? this.filterParams?.tax_year ?? this.rowData?.TAX_YEAR,
      SCENARIO: g.scenario ?? this.filterParams?.scenario ?? this.rowData?.SCENARIO,
      JCD_KEY: g.jcd_key ?? this.filterParams?.jcd_key ?? this.rowData?.JCD_KEY,
      TRIGGER_CHART_KEY: this.rowData.TRIGGER_CHART_KEY,
      TRIGGER_ACCT_KEY: this.rowData.TRIGGER_ACCT_KEY,
      TEMPLATE_ID: newtemplate_key ?? this.rowData.SCHDM_TEMPLATE_ID,
      SCHDM_TYPE: new_schdm_key ?? this.rowData.SCHDM_TYPE,
      TBBS_ACCT_KEY: new_tbbs_acct_key ?? this.rowData.TBBS_ACCT_KEY,
      MULTIPLIER: this.multiplier,
      USER_DESC: this.userm1,
      POSTING_TAC_KEY: this.rowData.POSTING_TAC_KEY,
      POSTING_ACCT_KEY: this.rowData.POSTING_ACCT_KEY,
      METHOD_OF_CALC: this.rowData.METHOD_OF_CALC,
      PROVISION_YN: this.rowData.PROVISION_YN
    };
    const sendobj = [dummyJson];

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    this.service.saveRecommAutomation(
      this.GENERAL_CONFIG.base_url,
      sendobj,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(result?.errorMessage || 'Save failed', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbar.openSnackBar('Data has been saved successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  reset(): void {
    this.init();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
